import type {
  ButtonHTMLAttributes,
  HTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
} from 'react'
import { space, type Space } from '@cottonray/design-tokens'

const cx = (...parts: (string | false | undefined)[]): string =>
  parts.filter((part): part is string => typeof part === 'string').join(' ')

export type TextVariant = 'display' | 'title' | 'heading' | 'body' | 'label' | 'caption'
export type Tone = 'default' | 'muted' | 'subtle' | 'accent' | 'danger' | 'success'

const TEXT_TAGS = {
  display: 'h1',
  title: 'h2',
  heading: 'h3',
  body: 'p',
  label: 'span',
  caption: 'span',
} as const

export interface TextProps extends HTMLAttributes<HTMLElement> {
  variant?: TextVariant
  tone?: Tone
  /**
   * 태그만 바꾸고 크기는 `variant` 를 따른다 — "제목인데 h1 만큼 크지는 않은" 자리에 쓴다.
   *
   * **variant 이름이 아니라 태그 이름이다.** 이 값은 그대로 엘리먼트가 되므로
   * (`const Tag = as ?? TEXT_TAGS[variant]`), 예전 타입처럼 `'display'` 를 받으면
   * `<display>` 라는 없는 태그가 그려진다. 실제로 쓰는 태그만 적는다
   */
  as?: 'h1' | 'h2' | 'h3' | 'p' | 'div' | 'span'
}

export function Text({
  variant = 'body',
  tone = 'default',
  as,
  className,
  ...rest
}: TextProps) {
  const Tag = (as ?? TEXT_TAGS[variant]) as 'p'
  return (
    <Tag
      className={cx('st-text', `st-text--${variant}`, `st-tone--${tone}`, className)}
      {...rest}
    />
  )
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost'
  size?: 'sm' | 'md' | 'lg'
  fullWidth?: boolean
}

export function Button({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  className,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cx(
        'st-btn',
        `st-btn--${variant}`,
        `st-btn--${size}`,
        fullWidth && 'st-btn--full',
        className,
      )}
      {...rest}
    />
  )
}

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  raised?: boolean
}

export function Card({ raised = false, className, ...rest }: CardProps) {
  return <div className={cx('st-card', raised && 'st-card--raised', className)} {...rest} />
}

interface StackProps extends HTMLAttributes<HTMLDivElement> {
  gap?: Space
  align?: string
  justify?: string
  children?: ReactNode
}

function Stack({
  direction,
  gap = 0,
  align,
  justify,
  className,
  style,
  ...rest
}: StackProps & { direction: 'row' | 'column' }) {
  return (
    <div
      className={cx('st-stack', `st-stack--${direction}`, className)}
      style={{ gap: space[gap], alignItems: align, justifyContent: justify, ...style }}
      {...rest}
    />
  )
}

export function VStack(props: StackProps) {
  return <Stack direction="column" {...props} />
}

export function HStack(props: StackProps) {
  return <Stack direction="row" {...props} />
}

export function Container({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cx('st-container', className)} {...rest} />
}

/**
 * 네이티브 `<select>`를 쓴다 — 직접 만든 드롭다운은 키보드 조작·스크린리더·모바일
 * 휠 피커를 다시 구현해야 한다. 언어 선택처럼 목록이 짧고 고정된 곳에 맞는 선택이다.
 */
export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  /** 네이티브 `size`(보이는 줄 수)를 가린다 — 드롭다운으로만 쓰므로 줄 수는 필요 없다 */
  size?: 'sm' | 'md'
}

export function Select({ size = 'md', className, ...rest }: SelectProps) {
  return <select className={cx('st-select', `st-select--${size}`, className)} {...rest} />
}

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: 'neutral' | 'accent' | 'success' | 'danger'
}

export function Badge({ tone = 'neutral', className, ...rest }: BadgeProps) {
  return <span className={cx('st-badge', `st-badge--${tone}`, className)} {...rest} />
}

/**
 * 로딩 자리 — `ui-native`의 Skeleton과 같은 이름·같은 두 토큰(`skeleton`·`skeleton-strong`)이다.
 * 색을 늘리거나 테두리를 넣지 말 것. 위계는 크기·너비·개수로만 낸다 (DESIGN.md §Colors).
 *
 * **읽어 주는 기계를 위해**: 로딩 중이라는 사실은 시각적 펄스로만 전하면 스크린리더에 아무것도
 * 안 들린다. 자리 표시는 `aria-hidden`으로 감추고, 바깥을 `role="status"`로 감싸 텍스트 한 줄을
 * 함께 준다 — `SkeletonBlock`이 그 역할까지 한다.
 */
export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  width?: number | string
  height?: number
}

export function Skeleton({ width = '100%', height = 16, style, className, ...rest }: SkeletonProps) {
  return (
    <div
      className={cx('st-skeleton', className)}
      aria-hidden
      style={{ width, height, ...style }}
      {...rest}
    />
  )
}

export interface SkeletonTextProps {
  /** 줄 수 — 마지막 줄은 짧게 그려 문단처럼 보이게 한다 */
  lines?: number
}

export function SkeletonText({ lines = 3 }: SkeletonTextProps) {
  return (
    <VStack gap={2}>
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          className="st-skeleton st-skeleton--line"
          aria-hidden
          style={{ width: i === lines - 1 ? '60%' : '100%' }}
        />
      ))}
    </VStack>
  )
}

export interface SkeletonCardProps {
  lines?: number
}

/** 카드 한 장의 실루엣. 목록 자리에 여러 장 쌓아 쓴다 */
export function SkeletonCard({ lines = 3 }: SkeletonCardProps) {
  return (
    <VStack gap={3} className="st-skeleton--card">
      <Skeleton width="50%" height={20} />
      <SkeletonText lines={lines} />
    </VStack>
  )
}

export interface SkeletonBlockProps extends SkeletonCardProps {
  /** 스크린리더에 읽히는 문구. 화면에는 보이지 않는다 */
  label?: string
  /** 쌓을 카드 수 */
  cards?: number
}

/**
 * 화면 하나의 로딩 상태 통째 — 이걸 쓰면 접근성 배선을 매번 다시 하지 않아도 된다.
 * `불러오는 중…` 같은 텍스트 한 줄을 대체하는 자리다.
 */
export function SkeletonBlock({ label = '불러오는 중', cards = 2, lines = 3 }: SkeletonBlockProps) {
  return (
    <VStack gap={4} role="status" aria-busy="true" aria-label={label}>
      {Array.from({ length: cards }).map((_, i) => (
        <SkeletonCard key={i} lines={lines} />
      ))}
    </VStack>
  )
}
